import * as z from 'zod';
import { optionalText, requiredText } from '@/modules/service-requests/shared/application-utils';
import { NocTypeRequirements } from '../utils/noc-rules';

/**
 * The schema depends on the chosen NOC type (visa NOCs need a passport and travel details), so it
 * is built from that type's requirements.
 */
export const createNocApplySchema = (requirements: NocTypeRequirements) =>
  z
    .object({
      nocTypeId: z.string().trim().min(1, { message: 'NOC_TYPE_REQUIRED' }),
      nocCopyType: z.enum(['HardCopy', 'SoftCopy', 'Booth'], {
        error: 'NOC_COPY_TYPE_REQUIRED',
      }),
      firstName: requiredText('FIRST_NAME_REQUIRED', 100),
      lastName: requiredText('LAST_NAME_REQUIRED', 100),
      designation: requiredText('DESIGNATION_REQUIRED', 150),
      passportNumber: optionalText(30),
      startDate: z.string(),
      endDate: z.string(),
      travelDestination: optionalText(200),
      reason: requiredText('REASON_REQUIRED', 2000),
    })
    .superRefine((values, context) => {
      const missing = (value: string) => !value.trim();
      const require = (path: keyof typeof values, message: string) =>
        context.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });

      if (requirements.passportRequired && missing(values.passportNumber)) {
        require('passportNumber', 'PASSPORT_NUMBER_REQUIRED');
      }
      if (requirements.datesRequired && missing(values.startDate)) {
        require('startDate', 'TRAVEL_START_DATE_REQUIRED');
      }
      if (requirements.datesRequired && missing(values.endDate)) {
        require('endDate', 'TRAVEL_END_DATE_REQUIRED');
      }
      if (requirements.destinationRequired && missing(values.travelDestination)) {
        require('travelDestination', 'TRAVEL_DESTINATION_REQUIRED');
      }
      if (values.startDate.trim() && values.endDate.trim() && values.endDate < values.startDate) {
        require('endDate', 'TRAVEL_END_DATE_BEFORE_START_DATE');
      }
    });

export type NocApplyValues = z.infer<ReturnType<typeof createNocApplySchema>>;
